import type { ErrorEvent as SentryErrorEvent, EventHint, StackFrame } from '@sentry/vue';
import type { ThrownObject } from '@core/analytics/interfaces/logger';
import type { SentryEventDecision, SentryExpectedFailure } from '@core/analytics/types/sentry';
import {
  APP_REPORT_TAG,
  IGNORED_ERROR_SNIPPETS,
  IGNORED_EXCEPTION_TYPES,
  MASKED_VALUE,
  MAX_CAUSE_DEPTH,
  MAX_MASK_DEPTH,
  NETWORK_FAILURE_MESSAGES,
  NETWORK_FAILURE_NAMES,
  PLACEHOLDER_FRAME_FILENAMES,
  QUERY_PARAM_PATTERN,
  SENSITIVE_KEY_NAMES,
  SENSITIVE_KEY_PATTERN,
  STALE_BUILD_MESSAGES,
  URL_FIELD_NAMES,
} from '../constants/sentry';

export const getAppOrigin = () => {
  if (typeof location === 'undefined') {
    return '';
  }

  const { origin, protocol, host } = location;

  if (origin && origin !== 'null') {
    return origin;
  }

  return protocol && host ? `${protocol}//${host}` : '';
};

const isBuildAsset = (filename: string, appAssetsUrl: string) => {
  try {
    return new URL(filename, location.href).href.startsWith(appAssetsUrl);
  } catch {
    return false;
  }
};

const findThrowSite = (frames: StackFrame[]) => {
  for (let index = frames.length - 1; index >= 0; index -= 1) {
    const { filename, lineno } = frames[index]!;

    if (lineno !== undefined || !PLACEHOLDER_FRAME_FILENAMES.has(filename ?? '')) {
      return frames[index];
    }
  }

  return undefined;
};

export const listCauseChain = (thrown: unknown): ThrownObject[] => {
  const links: ThrownObject[] = [];
  let link = thrown;

  while (links.length < MAX_CAUSE_DEPTH && typeof link === 'object' && link !== null) {
    links.push(link as ThrownObject);
    link = (link as ThrownObject).cause;
  }

  return links;
};

export const describeExpectedFailure = (thrown: unknown): SentryExpectedFailure | undefined => {
  for (const { name, message, status } of listCauseChain(thrown)) {
    const text = typeof message === 'string' ? message : '';

    if (STALE_BUILD_MESSAGES.some(snippet => text.includes(snippet))) {
      return 'stale_build';
    }

    if (typeof status === 'number') {
      return 'api_response';
    }

    if (NETWORK_FAILURE_NAMES.has(String(name)) || NETWORK_FAILURE_MESSAGES.some(prefix => text.startsWith(prefix))) {
      return 'network_failure';
    }
  }

  return undefined;
};

export const checkEvent = (
  event: SentryErrorEvent,
  hint: EventHint | undefined,
  appAssetsUrl: string,
): SentryEventDecision => {
  const thrown = event.exception?.values;
  const exception = thrown?.[thrown.length - 1];
  const message = exception?.value ?? '';
  const original = hint?.originalException;
  const messages = [message, event.message, original instanceof Error ? original.message : String(original ?? '')];

  if (messages.some(text => IGNORED_ERROR_SNIPPETS.some(snippet => text?.includes(snippet)))) {
    return { action: 'drop', reason: 'proven_browser_noise' };
  }

  const expectedFailure = describeExpectedFailure(original);

  if (expectedFailure) {
    return { action: 'drop', reason: expectedFailure };
  }

  if (IGNORED_EXCEPTION_TYPES.has(exception?.type ?? '')) {
    return { action: 'drop', reason: 'ignored_exception_type' };
  }

  if (event.tags?.[APP_REPORT_TAG.name] === APP_REPORT_TAG.value) {
    return { action: 'keep', reason: 'app_reported' };
  }

  if (!exception) {
    return { action: 'keep', reason: 'message_event' };
  }

  const frames = exception.stacktrace?.frames ?? [];

  if (!frames.length) {
    return { action: 'drop', reason: 'empty_stack' };
  }

  if (!appAssetsUrl) {
    return { action: 'keep', reason: 'unknown_origin' };
  }

  const throwSite = findThrowSite(frames)?.filename;

  return throwSite && isBuildAsset(throwSite, appAssetsUrl)
    ? { action: 'keep', reason: 'app_code' }
    : { action: 'drop', reason: 'foreign_throw_site' };
};

const isSensitiveKey = (key: string) =>
  SENSITIVE_KEY_PATTERN.test(key) || SENSITIVE_KEY_NAMES.has(key.toLowerCase());

const maskUrl = (url: string) => url.replace(
  QUERY_PARAM_PATTERN,
  (param, separator: string, keyAndValue: string) => {
    const [key = ''] = keyAndValue.split('=');

    return isSensitiveKey(key) ? `${separator}${key}=${MASKED_VALUE}` : param;
  },
);

const maskFields = (fields: Record<string, unknown> | undefined, depth = 0) => {
  if (!fields) {
    return;
  }

  Object.entries(fields).forEach(([name, value]) => {
    if (isSensitiveKey(name)) {
      fields[name] = MASKED_VALUE;
      return;
    }

    if (typeof value === 'string' && URL_FIELD_NAMES.has(name)) {
      fields[name] = maskUrl(value);
      return;
    }

    if (value && typeof value === 'object' && depth < MAX_MASK_DEPTH) {
      maskFields(value as Record<string, unknown>, depth + 1);
    }
  });
};

export const maskPersonalData = (event: SentryErrorEvent): SentryErrorEvent => {
  const { request, breadcrumbs, extra } = event;

  if (request?.url) {
    request.url = maskUrl(request.url);
  }

  maskFields(request?.headers);
  maskFields(extra);
  breadcrumbs?.forEach(note => maskFields(note.data));

  return event;
};
